import {
    BarChart3,
    ClipboardList,
    Container,
    FileText,
    NotebookPen,
    Ship,
    Truck,
} from "lucide-react";
import { formatReportCurrency } from "@/utility/fclReportHelpers";
import {
    IMPORT_CARRIER_TEUS_COLUMNS,
    IMPORT_CONTAINER_TEUS_COLUMNS,
    IMPORT_CONTAINER_TRACKING_COLUMNS,
    IMPORT_DEMURRAGE_LIST_COLUMNS,
    IMPORT_SALES_PERSON_TEUS_COLUMNS,
    IMPORT_STATUS_AUDIT_COLUMNS,
    IMPORT_TRUCKING_COLUMNS,
} from "./import/importReportColumns";
import {
    IMPORT_BOOKING_COMPLEXITY_SUMMARY_COLUMNS,
    IMPORT_BOOKING_LIST_COLUMNS,
} from "./import/importBookingReportColumns";
import { IMPORT_ACTIVE_SHIPMENT_LIST_COLUMNS } from "./import/importActiveShipmentReportColumns";
import { IMPORT_ARRIVAL_NOTICE_LIST_COLUMNS } from "./import/importArrivalNoticeReportColumns";
import { IMPORT_ARRIVAL_NOTICE_PRE_ALERT_LIST_COLUMNS } from "./import/importArrivalNoticePreAlertReportColumns";
import { IMPORT_ETA_STATUS_LIST_COLUMNS } from "./import/importEtaStatusReportColumns";
import { IMPORT_FILE_CANCELLATION_LIST_COLUMNS } from "./import/importFileCancellationReportColumns";

const metric = (keys, label, format, icon, colorIndex) => ({
    keys: Array.isArray(keys) ? keys : [keys],
    label,
    format,
    icon,
    colorIndex,
});

const formatMetricValue = (summary, m) => {
    for (const key of m.keys) {
        const val = summary?.[key];
        if (val === null || val === undefined || val === "") continue;
        if (m.format === "currency") return formatReportCurrency(val);
        if (m.format === "number") {
            const num = Number(String(val).replace(/[^0-9.-]/g, ""));
            return Number.isNaN(num) ? String(val) : num.toLocaleString("en-US");
        }
        return String(val);
    }
    return m.format === "number" ? "0" : "-";
};

export const resolveReportMetrics = (summary = {}, metrics = []) =>
    metrics.map((m) => ({
        ...m,
        value: formatMetricValue(summary, m),
    }));

/** @type {Record<string, object>} */
export const FCL_REPORT_DEFINITIONS = {
    importBooking: {
        summaryTitle: "Import Booking Report Summary",
        listTitle: "Booking List",
        listColumns: IMPORT_BOOKING_LIST_COLUMNS,
        metrics: [],
        useCompactGrid: true,
        sectionsAfterList: true,
        sections: [
            {
                id: "bookingComplexitySummary",
                title: "Booking Customer Wise Complexity Summary Report",
                columns: IMPORT_BOOKING_COMPLEXITY_SUMMARY_COLUMNS,
                icon: FileText,
            },
        ],
    },
    importActiveShipment: {
        summaryTitle: "Active Shipment Report Summary",
        listTitle: "Active Shipment List",
        listColumns: IMPORT_ACTIVE_SHIPMENT_LIST_COLUMNS,
        metrics: [],
        useCompactGrid: true,
    },
    importDemurrage: {
        summaryTitle: "Container Demurrage Report Summary",
        listTitle: "Demurrage List",
        listColumns: IMPORT_DEMURRAGE_LIST_COLUMNS,
        metrics: [],
        useCompactGrid: true,
    },
    importTrucking: {
        summaryTitle: "Trucking Report Summary",
        listTitle: "Trucking List",
        listColumns: IMPORT_TRUCKING_COLUMNS,
        metrics: [
            metric(["totalRecords", "TotalRecords", "totalTrucking", "TotalTrucking"], "Total Records", "number", Truck, 0),
        ],
        useCompactGrid: true,
    },
    importStatusAudit: {
        summaryTitle: "Status Audit Report Summary",
        listTitle: "Status Audit List",
        listColumns: IMPORT_STATUS_AUDIT_COLUMNS,
        metrics: [
            metric(["totalRecords", "TotalRecords", "totalAudit", "TotalAudit"], "Total Records", "number", ClipboardList, 0),
        ],
        useCompactGrid: true,
    },
    importContainerTracking: {
        summaryTitle: "Container Tracking Report Summary",
        listTitle: "Container Tracking List",
        listColumns: IMPORT_CONTAINER_TRACKING_COLUMNS,
        metrics: [
            metric(["totalContainers", "TotalContainers"], "Total Containers", "number", Container, 0),
            metric(["totalRecords", "TotalRecords"], "Total Records", "number", ClipboardList, 1),
        ],
        useCompactGrid: true,
    },
    importArrivalNotice: {
        summaryTitle: "Arrival Notice Report Summary",
        listTitle: "Arrival Notice List",
        listColumns: IMPORT_ARRIVAL_NOTICE_LIST_COLUMNS,
        metrics: [],
        useCompactGrid: true,
    },
    importEtaStatus: {
        summaryTitle: "ETA Status Report Summary",
        listTitle: "ETA STATUS REPORT",
        listColumns: IMPORT_ETA_STATUS_LIST_COLUMNS,
        metrics: [],
        useCompactGrid: true,
    },
    importFileCancellationReport: {
        summaryTitle: "File Cancellation Report Summary",
        listTitle: "File Cancellation List",
        listColumns: IMPORT_FILE_CANCELLATION_LIST_COLUMNS,
        metrics: [
            metric(["totalRecords", "TotalRecords", "totalCancellation", "TotalCancellation"], "Total Records", "number", ClipboardList, 0),
        ],
        useCompactGrid: true,
    },
    importArrivalNoticePreAlertReport: {
        summaryTitle: "Arrival Notice Pre-Alert Report Summary",
        listTitle: "Arrival Notice List",
        listColumns: IMPORT_ARRIVAL_NOTICE_PRE_ALERT_LIST_COLUMNS,
        metrics: [],
        useCompactGrid: true,
    },
};

export const getReportDefinition = (reportKey) => FCL_REPORT_DEFINITIONS[reportKey] ?? null;
